// src/components/ResetPassword/ResetPassword.js
import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import CustomModal from "../../components/CustomModal/CustomModal";
import "./ResetPassword.css";
import conectaLogo from "../../assets/generales/conecta-peru-footer.svg";
import resetPasswordHombre from "../../assets/generales/conectapersona.svg";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    message: "",
    type: "",
  });

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const newPasswordIsValid = newPassword.length >= 6;
  const passwordsMatch = newPassword === confirmPassword;
  const isFormValid = newPasswordIsValid && passwordsMatch;

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    setNewPasswordTouched(true);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordTouched(true);
  };

  const openModal = (title, message, type = "info") => {
    setModalContent({ title, message, type });
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    if (modalContent.type === "success") {
      navigate("/login");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!token) {
      openModal(
        "Error de Token",
        "El enlace para restablecer la contraseña es inválido o no tiene token.",
        "error"
      );
      return;
    }

    if (!isFormValid) {
      openModal(
        "Contraseñas no válidas",
        "Por favor, asegúrate de que las contraseñas coincidan y tengan al menos 6 caracteres.",
        "error"
      );
      return;
    }

    setIsResetting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/loguin/reset-password`,
        {
          token: token,
          nuevaContrasenia: newPassword,
        }
      );

      if (response.status === 200) {
        openModal("Contraseña Cambiada", response.data.message, "success");
      }
    } catch (error) {
      console.error("Error al restablecer contraseña:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Ocurrió un error. El código puede ser inválido o haber expirado.";
      openModal("Error", errorMessage, "error");
    } finally {
      setIsResetting(false);
    }
  };

  const getValidationClass = (touched, isValid) => {
    if (!touched) return "";
    return isValid ? "is-valid" : "is-invalid";
  };

  return (
    <div className="reset-root row g-0 min-vh-100">
      <div className="col-md-6 d-none d-md-block reset-img-col p-0">
        <img
          src={resetPasswordHombre}
          alt="Restablecer Contraseña"
          className="reset-img w-100 h-100"
        />
      </div>

      <div className="col-12 col-md-6 d-flex align-items-center justify-content-center reset-form-bg">
        <div className="reset-form-box w-100">
          <div className="text-center mb-4">
            <Link to="/">
              <img
                src={conectaLogo}
                alt="Conéctate Perú"
                className="reset-title-img mb-3"
              />
            </Link>
            <h2 className="reset-heading mb-4">Cambiar Contraseña</h2>
          </div>

          <form onSubmit={handleResetPassword}>
            {/* Nuevo campo de Nueva Contraseña con ícono de ojo */}
            <div className="form-floating mb-3 password-container">
              <input
                type={showNewPassword ? "text" : "password"}
                className={`form-control ${getValidationClass(
                  newPasswordTouched,
                  newPasswordIsValid
                )}`}
                id="floatingNuevaContrasenia"
                placeholder="Nueva Contraseña"
                value={newPassword}
                onChange={handleNewPasswordChange}
                onBlur={() => setNewPasswordTouched(true)}
                required
              />
              <label htmlFor="floatingNuevaContrasenia">Nueva Contraseña</label>
              <button
                className="btn btn-eye-toggle"
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                <i
                  className={`bi ${
                    showNewPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                  }`}
                ></i>
              </button>
              {newPasswordTouched && !newPasswordIsValid && (
                <div className="invalid-feedback d-block mt-2">
                  La contraseña debe tener al menos 6 caracteres.
                </div>
              )}
            </div>

            {/* Nuevo campo de Confirmar Contraseña con ícono de ojo */}
            <div className="form-floating mb-4 password-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className={`form-control ${getValidationClass(
                  confirmPasswordTouched,
                  passwordsMatch
                )}`}
                id="floatingConfirmarContrasenia"
                placeholder="Confirmar Contraseña"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={() => setConfirmPasswordTouched(true)}
                required
              />
              <label htmlFor="floatingConfirmarContrasenia">
                Confirmar Contraseña
              </label>
              <button
                className="btn btn-eye-toggle"
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <i
                  className={`bi ${
                    showConfirmPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                  }`}
                ></i>
              </button>
              {confirmPasswordTouched && !passwordsMatch && (
                <div className="invalid-feedback d-block mt-2">
                  Las contraseñas no coinciden.
                </div>
              )}
            </div>

            <div className="d-grid mb-3">
              <button
                type="submit"
                className="btn-login-aceptar"
                disabled={isResetting || !isFormValid}
              >
                {isResetting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {" Cambiando..."}
                  </>
                ) : (
                  "Cambiar Contraseña"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <CustomModal
        isOpen={modalIsOpen}
        title={modalContent.title}
        message={modalContent.message}
        type={modalContent.type}
        onClose={closeModal}
      />
    </div>
  );
};

export default ResetPassword;
